import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BOOKING_URL } from "@/content/site";
import {
  parseAnalyticsEventNames,
  trackEvents,
  type AnalyticsEventName,
  type AnalyticsProperties,
} from "@/lib/analytics";

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "") || "/";

const isConfiguredBookingHref = (href: string) => {
  try {
    const configured = new URL(BOOKING_URL);
    const clicked = new URL(href, window.location.href);

    return configured.origin === clicked.origin
      && trimTrailingSlash(configured.pathname) === trimTrailingSlash(clicked.pathname);
  } catch {
    return false;
  }
};

const slugFromPath = (pathname: string, prefix: string) => {
  const normalizedPrefix = trimTrailingSlash(prefix);
  if (!pathname.startsWith(`${normalizedPrefix}/`)) return undefined;

  return pathname.slice(normalizedPrefix.length + 1).split("/")[0] || undefined;
};

const targetPathFromHref = (href?: string | null) => {
  if (!href) return undefined;

  try {
    return new URL(href, window.location.href).pathname;
  } catch {
    return undefined;
  }
};

const collectAnalyticsProperties = (
  eventElement: HTMLElement,
  pathname: string,
  anchor?: HTMLAnchorElement | null,
): AnalyticsProperties => {
  const serviceSlug = eventElement.dataset.eventServiceSlug ?? slugFromPath(pathname, "/services");
  const industrySlug = eventElement.dataset.eventIndustrySlug ?? slugFromPath(pathname, "/industries");
  const articleSlug = eventElement.dataset.eventArticleSlug ?? slugFromPath(pathname, "/insights");
  const resourceSlug = eventElement.dataset.eventResourceSlug;

  return {
    event_location: eventElement.dataset.eventLocation,
    event_surface: eventElement.dataset.eventSurface,
    event_target: eventElement.dataset.eventTarget,
    path: pathname,
    target_path: targetPathFromHref(anchor?.getAttribute("href")),
    service_slug: serviceSlug,
    industry_slug: industrySlug,
    article_slug: articleSlug,
    resource_slug: resourceSlug,
  };
};

const AnalyticsListener = () => {
  const location = useLocation();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;

      const eventElement = event.target.closest<HTMLElement>("[data-event]");
      const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
      const eventNames = parseAnalyticsEventNames(eventElement?.dataset.event);
      const eventsToTrack: AnalyticsEventName[] = [...eventNames];

      if (anchor && isConfiguredBookingHref(anchor.href) && !eventsToTrack.includes("booking_cta_click")) {
        eventsToTrack.push("booking_cta_click");
      }

      if (eventsToTrack.length === 0) return;

      const properties = eventElement
        ? collectAnalyticsProperties(eventElement, location.pathname, anchor)
        : { path: location.pathname, target_path: targetPathFromHref(anchor?.getAttribute("href")) };

      trackEvents(eventsToTrack, properties);
    };

    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [location.pathname]);

  return null;
};

export default AnalyticsListener;
